import { Fragment, useEffect, useState } from 'react';
import { Typography, Card, Grid, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { addDays, differenceInMonths, endOfMonth, format, parse, startOfMonth, subDays } from 'date-fns';
import { getDashboardData, selectDashboardState } from '../dashboard/sagas/pmDashboardSlice';
import { selectOnboardingApplicationState } from '../onboarding/sagas/pmonboardingSlice';
import { capitalizeFirstLetter } from '../PmUI';
import NumberFormat from 'react-number-format';
import { selectAuthState, selectCurrentUser } from '../../shared/sagas/auth/authSlice';
import MonthPicker from './components/CustomMonthPicker';
import AcknowledgmentModal from '../acknowledgements/components/AcknowledgmentModal';
import { getAcknowledgements, markAcknowledgementAsResolved, resetAcknowledgmentResolvedStates, selectAcknowledgementsState } from '../acknowledgements/sagas/acknowledgementsSlice';
import { AcknowledgementV2Dto } from '@inosio/payments-sdk';
import PciAcknowledgement from '../acknowledgements/components/PciAcknowledgement';

const dateFormat = 'yyyy-MM-dd';

function Dashboard() {
  const [ackModalsOpenStates, setAckModalsOpenStates] = useState<Record<string, boolean>>({}); //Maps UUID to open state of the individual modal
  const { userDetails, isUpdateUserDetailsSuccess } = useSelector(selectAuthState);
  const currentUser = useSelector(selectCurrentUser);

  const dispatch = useDispatch();

  useEffect(() => {
    if (isUpdateUserDetailsSuccess) {
      dispatch(getDashboardData({}));
    }
  }, [dispatch, isUpdateUserDetailsSuccess]);

  const dashboardData = useSelector(selectDashboardState);
  const selectOnboardingState = useSelector(selectOnboardingApplicationState);

  const [formattedStartDate, setFormattedStartDate] = useState<string | null>(null);
  const [formattedEndDate, setFormattedEndDate] = useState<string | null>(null);
  const [displayedMonth, setDisplayedMonth] = useState('');
  const [disableBack, setDisableBack] = useState(false);
  const [disableForward, setDisableForward] = useState(false);

  useEffect(() => {
    if (dashboardData.startDate && dashboardData.endDate) {
      const startDate = parse(dashboardData.startDate, dateFormat, new Date());
      setFormattedStartDate(format(startDate, 'PP'));
      const endDate = parse(dashboardData.endDate, dateFormat, new Date());
      setFormattedEndDate(format(endDate, 'PP'));
    }
  }, [dashboardData]);

  const { acknowledgements, isMarkingAcknowledgmentAsResolvedSuccess } = useSelector(selectAcknowledgementsState);

  const allPendingAcknowledments = acknowledgements.filter((ack) => ack.TypeId === 1); //acknowledgment type id 1 is a Single Modal With X and single button
  const allPendingPciAcknowledgments = acknowledgements.filter((ack) => ack.TypeId === 2); //acknowledgment type id 2 is a PCI UI Wizard with two screens

  useEffect(() => {
    if (!!allPendingAcknowledments.length) {
      // Initialize modal states for new acknowledgments
      const initialAckModalStates: Record<string, boolean> = allPendingAcknowledments.reduce((acc, ack) => {
        acc[ack.Uuid] = acc[ack.Uuid] ?? true; // Use existing state if available; default to true; // Preserve existing state or default to false
        return acc;
      }, {} as Record<string, boolean>);
      setAckModalsOpenStates(initialAckModalStates);
    }
  }, [acknowledgements]);

  //if marking an acknowledgment is a success, fetch a fresh copy of all pending acknowledgments
  useEffect(() => {
    if (isMarkingAcknowledgmentAsResolvedSuccess) {
      dispatch({
        type: getAcknowledgements.type,
        payload: {},
      });
      dispatch(resetAcknowledgmentResolvedStates());
    }
  }, [isMarkingAcknowledgmentAsResolvedSuccess]);

  useEffect(() => {
    if (formattedStartDate) {
      const startDate = parse(formattedStartDate, 'PP', new Date());
      setDisplayedMonth(format(startDate, 'LLLL'));

      const monthDiffs = differenceInMonths(startOfMonth(new Date()), startDate);
      if (monthDiffs === 0) {
        setDisableForward(true);
        setFormattedEndDate(format(new Date(), 'PP'));
      } else {
        setDisableForward(false);
      }

      if (monthDiffs === 12) {
        setDisableBack(true);
      } else {
        setDisableBack(false);
      }
    }
  }, [formattedStartDate]);

  const dataStyles = {
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '36px',
    lineHeight: '22px',
    color: '#14142B',
    marginBottom: '40px',
  };

  const residentsTitleStyles = {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '22px',
    color: ' #14142B',
  };
  const residentDataStyle = {
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '36px',
    lineHeight: '22px',
    color: ' #14142B',
    marginTop: '30px',
    marginBottom: '20px',
  };
  const transactionTitleStyle = {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '22px',
    color: '#14142B',
    marginBottom: '20px',
  };
  const refundsAndReturnsTitleStyle = {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: '22px',
    color: '#14142B',
    marginBottom: '40px',
  };

  const handlePrevMonth = () => {
    const parsedStartDate = parse(formattedStartDate as string, 'PP', new Date());
    const newEndDate = subDays(parsedStartDate, 1);
    setFormattedStartDate(format(startOfMonth(newEndDate), 'PP'));
    setFormattedEndDate(format(newEndDate, 'PP'));

    dispatch(
      getDashboardData({
        startDate: format(startOfMonth(newEndDate), dateFormat),
        endDate: format(newEndDate, dateFormat),
      }),
    );
  };

  const handleNextMonth = () => {
    const parsedEndDate = parse(formattedEndDate as string, 'PP', new Date());
    const newStartDate = addDays(parsedEndDate, 1);
    setFormattedStartDate(format(newStartDate, 'PP'));
    setFormattedEndDate(format(endOfMonth(newStartDate), 'PP'));

    dispatch(
      getDashboardData({
        startDate: format(newStartDate, dateFormat),
        endDate: format(endOfMonth(newStartDate), dateFormat),
      }),
    );
  };

  //dismissing is clicking on X or in the background
  const handleDismissAcknowledgmentModal = (ack: AcknowledgementV2Dto) => {
    //if modal is dismissed, mark the acknowledgment as viewed only if we treat dismissing as acknowledging for this particular acknowledgment
    if (currentUser?.currentProfile.isActing === false && !!ack.TreatDismissingAsAcking) dispatch(markAcknowledgementAsResolved({ uuid: ack.Uuid }));

    //only close the modal if we dont wanna prevent app usage
    if (!ack.PreventsAppUsage) {
      const updatedModalStates: Record<string, boolean> = { ...ackModalsOpenStates };
      updatedModalStates[ack.Uuid] = false; // Close modal for the specific acknowledgment only
      setAckModalsOpenStates(updatedModalStates);
    }
  };

  // This is the primary button which actually acknowledges the acknowledgment
  const handleClickButtonAcknowledgmentModal = (ack: AcknowledgementV2Dto) => {
    if (currentUser?.currentProfile.isActing === false) dispatch(markAcknowledgementAsResolved({ uuid: ack.Uuid }));
    const updatedModalStates: Record<string, boolean> = { ...ackModalsOpenStates };
    updatedModalStates[ack.Uuid] = false; // Close modal for the specific acknowledgment only
    setAckModalsOpenStates(updatedModalStates);
  };

  return (
    <Fragment>
      <Typography component="h1" variant="h6" noWrap sx={{ fontWeight: 400, fontFamily: 'Work Sans, sans-serif', fontStyle: 'normal', fontSize: '36px', color: '#14142B', p: 1, pl: 0, flexGrow: 1 }}>
        Hello, {capitalizeFirstLetter(userDetails?.FirstName ?? selectOnboardingState.pmDetails.CompanyStaff.FirstName)}
      </Typography>
      <Card sx={{ pr: 5, pl: 5, pt: 2, pb: 2, display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: '24px 24px 0 0' }} style={{ borderLeft: '12px solid  #39DB8D' }}>
        <Grid container style={{ borderBottom: '2px solid #DFDFDF', marginBottom: '-15px', padding: '20px 40px' }}>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...residentsTitleStyles }} data-e2e="registered-residents">
                Registered Residents
              </Typography>
              <Typography style={{ ...residentDataStyle }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.residents?.RegisteredResidents || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...residentsTitleStyles }}>Residents</Typography>
              <Typography style={{ ...residentDataStyle }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.residents?.TotalResidents || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>

          <Grid item xs={3}>
            <div>
              <Typography style={{ ...residentsTitleStyles }}>Properties</Typography>
              <Typography style={{ ...residentDataStyle }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.residents?.PropertyCounts || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Card>
      <Card sx={{ pr: 5, pl: 5, pt: 2, pb: 2, display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: '0 0 0 0' }} style={{ borderLeft: '12px solid  #39A2DB' }}>
        <Grid container>
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }} marginTop={1} marginBottom={4}>
            <MonthPicker month={displayedMonth} disableBack={disableBack} disableForward={disableForward} handleBack={handlePrevMonth} handleForward={handleNextMonth} />
            <Typography style={{ fontFamily: 'Work Sans', fontStyle: 'normal', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#14142B', marginLeft: '14px' }}>
              {formattedStartDate} - {formattedEndDate}
            </Typography>
          </Grid>
        </Grid>
        <Grid container style={{ borderBottom: '2px solid #DFDFDF', marginBottom: '-15px', padding: '0 40px' }}>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...transactionTitleStyle }}>Total amount</Typography>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(dashboardData?.transactions?.totalTransactionsSum || 0) / 100}
                  prefix="$"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div style={{ borderRight: '2px solid #DFDFDF', marginRight: '100px' }}>
              <Typography style={{ ...transactionTitleStyle }}>ACH</Typography>
              <Typography style={{ ...dataStyles }}>{dashboardData?.transactions?.bank && dashboardData?.transactions?.bank?.percent}%</Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div style={{ borderRight: '2px solid #DFDFDF', marginRight: '100px' }}>
              <Typography style={{ ...transactionTitleStyle }}>Credit Card</Typography>
              <Typography style={{ ...dataStyles }}>{dashboardData?.transactions?.creditCard && dashboardData?.transactions?.creditCard?.percent}%</Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...transactionTitleStyle }}>Debit Card</Typography>
              <Typography style={{ ...dataStyles }}>{dashboardData?.transactions?.debitCard && dashboardData?.transactions?.debitCard?.percent}%</Typography>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography style={{ ...refundsAndReturnsTitleStyle }}>Total Payments</Typography>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(dashboardData?.transactions && dashboardData?.transactions?.totalTransactionsCount) || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.transactions?.bank?.totalTransactionsCount || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.transactions?.creditCard?.totalTransactionsCount || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.transactions?.debitCard?.totalTransactionsCount || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Card>
      <Card sx={{ pr: 5, pl: 5, pt: 2, pb: 2, display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: '0 0  24px 24px ' }} style={{ borderLeft: '12px solid  #F4B459' }}>
        <Grid container sx={{ padding: '0 40px' }}>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...refundsAndReturnsTitleStyle }}>Total ACH Returns</Typography>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(dashboardData?.returnsAndRefunds?.bankReturns?.totalTransactionsSum || 0) / 100}
                  prefix="$"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...refundsAndReturnsTitleStyle }}>Total Chargebacks</Typography>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(dashboardData?.returnsAndRefunds?.cardChargeBacks?.totalTransactionsSum || 0) / 100}
                  prefix="$"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...refundsAndReturnsTitleStyle }}>Total Refunds</Typography>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={(dashboardData.returnsAndRefunds?.refunds?.totalTransactionsSum || 0) / 100}
                  prefix="$"
                  decimalSeparator="."
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography style={{ fontFamily: 'Open Sans', fontStyle: 'normal', fontWeight: 600, fontSize: '18px', lineHeight: '22px', color: '#14142B', marginBottom: '30px' }}>
              Total Returned Payments
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.returnsAndRefunds?.bankReturns?.totalTransactionsCount || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.returnsAndRefunds?.cardChargeBacks?.totalTransactionsCount || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
          <Grid item xs={3}>
            <div>
              <Typography style={{ ...dataStyles }}>
                <NumberFormat
                  thousandsGroupStyle="thousand"
                  value={dashboardData?.returnsAndRefunds?.refunds?.totalTransactionsCount || 0}
                  displayType="text"
                  type="text"
                  thousandSeparator={true}
                  allowNegative={true}
                  decimalScale={0}
                  fixedDecimalScale={true}
                />
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Card>
      {allPendingAcknowledments.map((ack) => {
        return (
          <Box key={ack.Uuid}>
            <AcknowledgmentModal
              acknowledgmentDto={ack}
              open={ackModalsOpenStates[ack.Uuid] ?? false}
              handleDismiss={() => handleDismissAcknowledgmentModal(ack)}
              handleClickButton={() => handleClickButtonAcknowledgmentModal(ack)}
            />
          </Box>
        );
      })}
      {allPendingPciAcknowledgments.map((ack) => {
        return (
          <Box key={ack.Uuid}>
            <PciAcknowledgement acknowledgement={ack} />
          </Box>
        );
      })}
    </Fragment>
  );
}
export default Dashboard;
